import React from "react";
import {
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  Assignment,
  CheckCircle,
  LocalHospital,
  Description,
  RadioButtonUnchecked,
} from "@mui/icons-material";

// =============================================================================
// INTERFACE DO COMPONENTE
// =============================================================================

interface VetStepAreaProps {
  activeStep: number;
  setActiveStep: (step: number) => void;
}

// Definição das etapas do atendimento
interface Step {
  id: number;
  label: string;
  shortLabel: string;
  icon: React.ReactNode;
  description: string;
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Componente de navegação por etapas do atendimento veterinário
 * Usa cards interativos ao invés do stepper tradicional
 * 
 * @param activeStep - Índice da etapa ativa (0, 1 ou 2)
 * @param setActiveStep - Função para alterar a etapa ativa
 */
export const VetStepArea: React.FC<VetStepAreaProps> = ({
  activeStep,
  setActiveStep,
}) => {
  // Define as 3 etapas do atendimento veterinário
  const steps: Step[] = [
    {
      id: 0,
      label: "Coleta de dados",
      shortLabel: "Atendimento",
      icon: <LocalHospital />,
      description: "Anamnese e exame físico",
    },
    {
      id: 1,
      label: "Prescrições",
      shortLabel: "Prescrições",
      icon: <Assignment />,
      description: "Medicamentos e exames",
    },
    {
      id: 2,
      label: "Documentos",
      shortLabel: "Assinatura",
      icon: <Description />,
      description: "Assinatura e finalização",
    },
  ];

  // Calcula o progresso percentual
  const progress = ((activeStep + 1) / steps.length) * 100;

  /**
   * Retorna o ícone de status da etapa
   */
  const getStepStatusIcon = (stepIndex: number) => {
    if (stepIndex < activeStep) {
      return <CheckCircle sx={{ fontSize: 20, color: "#00A884" }} />;
    }
    if (stepIndex === activeStep) {
      return <RadioButtonUnchecked sx={{ fontSize: 20, color: "#00A884" }} />;
    }
    return <RadioButtonUnchecked sx={{ fontSize: 20, color: "#bdbdbd" }} />;
  };

  return (
    <Paper elevation={0} sx={{ p: 1.5, bgcolor: "transparent" }}>
      <Stack spacing={1.5}>
        {/* Barra de progresso */}
        <Box sx={{ width: "100%" }}>
          <Stack direction="row" justifyContent="space-between" mb={0.8}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Progresso do atendimento
            </Typography>
            <Typography variant="body2" color="primary" fontWeight={600}>
              {activeStep + 1} de {steps.length}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: "#e0e0e0",
              "& .MuiLinearProgress-bar": {
                bgcolor: "#00A884",
                borderRadius: 3,
              },
            }}
          />
        </Box>

        {/* Cards de navegação das etapas */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{ width: "100%" }}
        >
          {steps.map((step, index) => {
            const isActive = activeStep === index;
            const isCompleted = activeStep > index;
            const isPending = activeStep < index;

            return (
              <Button
                key={step.id}
                onClick={() => setActiveStep(index)}
                sx={{
                  flex: 1,
                  p: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  textAlign: "left",
                  textTransform: "none",
                  border: "2px solid",
                  borderRadius: 2,
                  transition: "all 0.3s ease",
                  position: "relative",
                  overflow: "hidden",
                  minHeight: 70,
                  // Estado ativo
                  ...(isActive && {
                    borderColor: "#00A884",
                    bgcolor: "rgba(0, 168, 132, 0.08)",
                    boxShadow: "0 4px 12px rgba(0, 168, 132, 0.2)",
                    transform: "translateY(-2px)",
                  }),
                  // Estado completo
                  ...(isCompleted && {
                    borderColor: "#00A884",
                    bgcolor: "rgba(0, 168, 132, 0.04)",
                  }),
                  // Estado pendente
                  ...(isPending && {
                    borderColor: "#e0e0e0",
                    bgcolor: "#fafafa",
                  }),
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                    borderColor: "#00A884",
                  },
                }}
              >
                {/* Indicador de status no canto superior direito */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                  }}
                >
                  {getStepStatusIcon(index)}
                </Box>

                {/* Ícone e Título na mesma linha */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ mb: 0.5, pr: 3 }}
                >
                  {/* Ícone da etapa */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      flexShrink: 0,
                      transition: "all 0.3s ease",
                      bgcolor: isActive
                        ? "#00A884"
                        : isCompleted
                        ? "#00A884"
                        : "#e0e0e0",
                      color: isActive || isCompleted ? "#fff" : "#757575",
                    }}
                  >
                    {step.icon}
                  </Box>

                  {/* Título da etapa */}
                  <Typography
                    variant="subtitle1"
                    fontWeight={isActive ? 700 : 600}
                    color={
                      isActive || isCompleted ? "#00A884" : "text.secondary"
                    }
                    sx={{ lineHeight: 1.3 }}
                  >
                    {step.label}
                  </Typography>
                </Stack>

                {/* Descrição da etapa */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.75rem", pl: 0.5 }}
                >
                  {step.description}
                </Typography>
              </Button>
            );
          })}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default VetStepArea;

